/**
 * localStorage that cannot throw.
 *
 * Reaching `window.localStorage` at all raises a SecurityError in some privacy
 * modes and sandboxed frames, and `setItem` raises QuotaExceededError once the
 * origin is full. Everything kept here — scores, stars, the chosen age, mute,
 * piece names — is a nicety: losing it is fine, failing a game over it is not.
 * So every read has a fallback and every write is best-effort.
 */

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readText(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeText(key: string, value: string): void {
  try {
    storage()?.setItem(key, value);
  } catch {
    // the preference just will not persist
  }
}

/** Stored number, or the fallback when the key is missing, blank or not a number. */
export function readNumber(key: string, fallback: number): number {
  const raw = readText(key);
  if (raw === null || raw.trim() === '') return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

/** Parsed JSON, or null when the key is missing or holds something unparseable. */
export function readJson(key: string): unknown {
  const raw = readText(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function writeJson(key: string, value: unknown): void {
  writeText(key, JSON.stringify(value));
}
